/**
 * Client-side taste vector + sound scoring for the personalized /sounds/foryou
 * feed. Pure TypeScript, no React — fully unit-testable.
 *
 * Inputs are all read from localStorage in the calling client island:
 *   - favoriteSoundIds   ← tls.favoriteSounds.v1
 *   - recent             ← tls.recent.v1 (RecentPlay[])
 *   - candidatePool      ← the SSR'd feed (used to hydrate favorite metadata)
 *
 * Scoring rules (from the For You feed plan):
 *   completed (recent)        → +1.0 category, +0.5 each vibe, +0.25 each tag
 *   skipped (recent)          → −0.5 category, −0.25 each vibe
 *   favorite (in pool)        → +2.0 category, +1.0 each vibe
 *   (no download events here  — those live in funnel_events, not localStorage,
 *    so the client can't see them yet; we surface the collaborative signal
 *    via Phase 3/4 instead.)
 *
 * Weights are normalized to [-1, +1] per bucket. A small exploration bonus
 * (~0.15) lifts unrepresented vibes/categories so the feed doesn't collapse
 * into a single bubble.
 */

import { soundInstallWarnings } from './compatibility'
import type { Sound } from './supabase-types'
import type { RecentPlay } from '@/hooks/useRecentPlays'

export type Signal = 'cold' | 'warm' | 'hot'

export interface TasteVector {
  categoryWeights: Record<string, number>
  vibeWeights: Record<string, number>
  tagWeights: Record<string, number>
  signal: Signal
}

export interface BuildTasteVectorInput {
  favoriteSoundIds: string[]
  recent: RecentPlay[]
  /**
   * Sounds available to look up metadata for favorite IDs. Pass the SSR'd
   * feed (trending + vibe blocks + bundles) — favorites NOT in this pool
   * contribute only an ID-level boost in scoreSound() but no taste signal.
   */
  candidatePool: Sound[]
}

export const EXPLORATION_BONUS = 0.15
const COMPLETED_CAT = 1.0
const COMPLETED_VIBE = 0.5
const COMPLETED_TAG = 0.25
const SKIPPED_CAT = -0.5
const SKIPPED_VIBE = -0.25
const FAVORITE_CAT = 2.0
const FAVORITE_VIBE = 1.0

function bump(map: Record<string, number>, key: string | null | undefined, amount: number) {
  if (!key) return
  map[key] = (map[key] ?? 0) + amount
}

function normalize(map: Record<string, number>): Record<string, number> {
  const entries = Object.entries(map)
  if (entries.length === 0) return {}
  const peak = Math.max(...entries.map(([, v]) => Math.abs(v)))
  if (peak === 0) return map
  const out: Record<string, number> = {}
  for (const [k, v] of entries) out[k] = v / peak
  return out
}

export function buildTasteVector(input: BuildTasteVectorInput): TasteVector {
  const { favoriteSoundIds, recent, candidatePool } = input
  const categoryWeights: Record<string, number> = {}
  const vibeWeights: Record<string, number> = {}
  const tagWeights: Record<string, number> = {}

  // 1) Recent plays — explicit positive/negative signal.
  for (const r of recent) {
    if (r.class === 'completed') {
      bump(categoryWeights, r.category, COMPLETED_CAT)
      for (const v of r.vibe_tags ?? []) bump(vibeWeights, v, COMPLETED_VIBE)
      for (const t of r.tags ?? []) bump(tagWeights, t, COMPLETED_TAG)
    } else if (r.class === 'skipped') {
      bump(categoryWeights, r.category, SKIPPED_CAT)
      for (const v of r.vibe_tags ?? []) bump(vibeWeights, v, SKIPPED_VIBE)
    }
  }

  // 2) Favorites — strongest positive (intentional act).
  // Only counts when we can resolve the sound's metadata via the candidate pool.
  const poolById = new Map(candidatePool.map((s) => [s.id, s]))
  for (const id of favoriteSoundIds) {
    const s = poolById.get(id)
    if (!s) continue
    bump(categoryWeights, s.category, FAVORITE_CAT)
    for (const v of s.vibe_tags ?? []) bump(vibeWeights, v, FAVORITE_VIBE)
  }

  const positiveSignals = recent.filter((r) => r.class === 'completed').length + favoriteSoundIds.length
  let signal: Signal = 'cold'
  if (positiveSignals >= 5) signal = 'hot'
  else if (positiveSignals >= 1) signal = 'warm'

  return {
    categoryWeights: normalize(categoryWeights),
    vibeWeights: normalize(vibeWeights),
    tagWeights: normalize(tagWeights),
    signal,
  }
}

/**
 * Returns a content-match score for `sound` against the user's taste.
 * Higher = better match. Score is roughly bounded but not formally clamped —
 * callers should compare relatively, not absolutely.
 *
 * Components:
 *   + categoryWeight[category]       (×1)
 *   + sum(vibeWeights[vibe])         (×1, dampened by vibe count)
 *   + sum(tagWeights[tag])           (×0.5)
 *   + small popularity prior log(downloads+1) / 10
 *   + +0.2 if the file installs as shipped (correct format, under the size cap)
 *   + +EXPLORATION_BONUS when the sound's category/vibe is unrepresented (0 weight)
 */
export function scoreSound(sound: Sound, taste: TasteVector): number {
  let score = 0

  const catWeight = taste.categoryWeights[sound.category] ?? 0
  score += catWeight
  if (catWeight === 0) score += EXPLORATION_BONUS

  const vibes = sound.vibe_tags ?? []
  if (vibes.length > 0) {
    const vibeScore = vibes
      .map((v) => taste.vibeWeights[v] ?? 0)
      .reduce((a, b) => a + b, 0) / vibes.length
    score += vibeScore
    if (vibeScore === 0) score += EXPLORATION_BONUS
  }

  const tags = sound.tags ?? []
  if (tags.length > 0) {
    const tagScore = tags
      .map((t) => taste.tagWeights[t] ?? 0)
      .reduce((a, b) => a + b, 0) / tags.length
    score += tagScore * 0.5
  }

  // Mild popularity prior — keeps cold-start ordering reasonable.
  const dl = Math.max(0, sound.downloads ?? 0)
  score += Math.log(dl + 1) / 10

  // Installability nudge: +0.2 when the file installs as shipped, so the
  // handful of non-WAV / oversized sounds sink below equivalent working ones.
  //
  // This replaces a vehicle-gated version of the same boost (2026-08-15). That
  // one also added +0.4 when `compatible_models` listed the visitor's model,
  // but every active sound lists all five models, so the term was a constant
  // added to every candidate and moved no ranking at all. The remaining
  // format/size signal is real, and now applies for every visitor instead of
  // only those who had filled in a vehicle form.
  if (soundInstallWarnings(sound).length === 0) score += 0.2

  return score
}

/**
 * Re-order an array of sounds by descending taste score. Stable for equal
 * scores so the caller's original ranking acts as a tiebreaker (preserving
 * the curated server ordering for cold-start visitors).
 */
export function reorderByTaste<T>(
  items: readonly T[],
  getSound: (item: T) => Sound,
  taste: TasteVector,
): T[] {
  return items
    .map((item, idx) => ({ item, idx, score: scoreSound(getSound(item), taste) }))
    .sort((a, b) => b.score - a.score || a.idx - b.idx)
    .map((x) => x.item)
}

/**
 * Re-order vibe blocks by descending mean vibe weight. Same stable-sort
 * fallback as reorderByTaste.
 */
export function reorderVibeBlocks<B extends { vibeSlug: string }>(
  blocks: readonly B[],
  taste: TasteVector,
): B[] {
  return blocks
    .map((b, idx) => ({
      b,
      idx,
      score: (taste.vibeWeights[b.vibeSlug] ?? 0) +
        ((taste.vibeWeights[b.vibeSlug] ?? 0) === 0 ? EXPLORATION_BONUS : 0),
    }))
    .sort((x, y) => y.score - x.score || x.idx - y.idx)
    .map((x) => x.b)
}
